import type { RootStoreProps } from "./stores/RootStore";
import type { ViewStoreOptions } from "@intrasync/stores/core/ViewStore";
import { LayoutType } from "types/LayoutType";
import { lazy } from "react";
import routesListArray from "./views/routesList";
import serviceStores from "serviceStoresConfig";
import appStores from "appStoresConfig";
import { env } from "./config/environment";
import { hqDiagnosticsOptions } from "./diagnostics/diagnosticsConfig";

const routesList = routesListArray.reduce(
  (acc: { [key: string]: typeof route }, route) => {
    if (route.name) {
      acc[route.name] = route;
    }
    return acc;
  },
  {}
);

// HQ's shell = the library AppLayout + HQ's toolbar actions (the notification
// bell), passed through the library's `toolbarActions` slot. See AppShell.
const LazyApp = lazy(() => import("./components/AppShell"));

// The host wrapper passes AuthLayout's options (emailDomain, title); keep it inside
// lazy() so the router never mistakes a bare component for an import thunk.
const LazyAuth = lazy(() => import("./views/auth/AuthLayoutHost"));

const layoutsListArray: LayoutType[] = [
  {
    name: "App",
    path: "/app",
    component: LazyApp,
    requiresAuth: false,
  },
  {
    name: "Auth",
    path: "/auth",
    component: LazyAuth as LayoutType["component"],
    requiresAuth: false,
  },
  { name: "Admin", path: "/admin", component: LazyApp, requiresAuth: true },
];

const layoutsList = layoutsListArray.reduce(
  (acc: { [key: string]: LayoutType }, layout) => {
    if (layout.name) {
      acc[layout.name] = layout;
    }
    return acc;
  },
  {}
);

/** HQ nav taxonomy, handed to the library AppLayoutStore via
 *  `appLayoutStore.navGroups` instead of being hardcoded in a forked store.
 *  It orders the OmniBar's page index, which is HQ's only navigation (no
 *  sidebar since 2026-10-31, plan/2026-10-31_DECISION_omnibar-first-nav.md).
 *  Group icons went with the sidebar: the OmniBar draws none. */
const HQ_NAV_GROUPS = [
  "Dashboard & Overview",
  "Clients",
  // Sits with Clients because that is what it bills. A group missing from this
  // list still renders — it just sorts alphabetically AFTER every listed group
  // (that is how Billing shipped).
  "Billing",
  "Modules",
  "Infrastructure",
  "DevOps",
  "Meeting Intelligence",
  // Client data imports (the Data Mapper moved into HQ, plan/2026-10-09).
  "Data Mapper",
  // Reports → Developer metrics (plan/2026-10-20_FROM-REPORT-METRICS_developer-metrics-service.md).
  "Reports",
  "AI",
  "Administration",
  "Admin",
  "Settings",
  // Not dead: ServiceViewRouteGenerator puts every auto-generated CRUD route in
  // "Management" (views/utils/ServiceViewRouteGenerator.ts).
  "Management",
];

/** HQ auth-token redirect hosts, injected into the library ViewStore through
 *  the TYPED `viewStoreOptions` prop (a typo here is a compile error; the
 *  untyped `viewStoreProps` bag next to it is not checked). */
const HQ_VIEW_STORE_OPTIONS: ViewStoreOptions = {
  authEndpoints: {
    dev: "http://localhost:3019/auth/{appName}-token",
    stage: "https://stage.intrasync.app/auth/{appName}-token",
    prod: "https://hq.intrasync.app/auth/{appName}-token",
  },
  autodeskAuthEndpoints: {
    dev: "http://localhost:3389/auth/autodesk/request-token?email={email}",
    development:
      "http://localhost:3389/auth/autodesk/request-token?email={email}",
    stage:
      "https://stage.hq-api.intrasync.app/auth/autodesk/request-token?email={email}",
    beta: "https://stage.hq-api.intrasync.app/auth/autodesk/request-token?email={email}",
    prod: "https://hq-api.intrasync.app/auth/autodesk/request-token?email={email}",
    production:
      "https://hq-api.intrasync.app/auth/autodesk/request-token?email={email}",
  },
};

/** The mode both the api client and the RootStore are built for. Exported so
 *  the two cannot drift — they used to be derived in two different files. */
export const hqMode: RootStoreProps["mode"] = env.isProduction
  ? "production"
  : env.isDevelopment
  ? "development"
  : "test";

/** Modes whose token storage keys carry NO `-${mode}` suffix. Must match the
 *  library's own list (RootStore's PRODUCTION_MODES) — see below. */
const PRODUCTION_MODES = ["production", "prod", "live"];
const storageKeyModeSuffix = PRODUCTION_MODES.includes(hqMode)
  ? ""
  : `-${hqMode}`;

/**
 * Token storage keys, derived ONCE and then pushed into both consumers.
 *
 * The api client reads/writes these keys before any store exists (socket auth
 * on first load, and the pre-connect refresh), while AuthStore reads them
 * after. The library RootStore derives its own defaults with exactly this rule,
 * but it also honours `enableAuthStore.{auth,refresh}StorageKey` and folds the
 * override into BOTH itself and `apiOptions` — so passing them explicitly is
 * what guarantees the client and the store can never disagree, whatever the
 * library's default becomes.
 */
export const authStorageKey = `feathers-jwt${storageKeyModeSuffix}`;
export const refreshStorageKey = `refresh-token${storageKeyModeSuffix}`;

/**
 * Options for `apiClient(hqMode, …)` — NOT for the RootStore.
 *
 * `RootStoreProps.apiOptions` is logging/URL configuration only; it has never
 * built a client, and the library requires the constructed client via
 * `props.app`. `enableSocketAuth` and the `test` uri override live here because
 * only the api client reads them (`RootStoreApiOptions` does not declare them).
 */
export const apiClientOptions = {
  debugMode: env.enableDebugMode,
  uriOverride: {
    development: env.apiUrl,
    production: env.apiUrl,
    test: env.apiUrl,
  },
  enableFindOne: true,
  enableSocketAuth: env.enableSocketAuth,
  authStorageKey,
  refreshStorageKey,
};

/**
 * Everything except `app`, which `createRootStore` constructs and adds — it is
 * a constructed Feathers client and cannot be expressed as static config.
 */
const rootStoreConfig: Omit<RootStoreProps, "app"> = {
  appName: env.appName,
  // storage: localforage,
  storage: localStorage,
  mode: hqMode,
  appVersion: env.appVersion,
  appTitle: "IntraSync",
  asyncStorage: true,
  autoUpdate: env.enableAutoUpdate,
  enableNotificationStore: true,
  enableRequestQueue: true,
  enableWebApiStore: true,
  enableBarcodeCameraStore: true,
  enableDataGridStore: true,
  enableScheduleGridStore: true,
  viewStoreOptions: HQ_VIEW_STORE_OPTIONS,
  viewStoreProps: { enableNotifications: true },
  syncChannels: true,
  storageName: env.appName,
  apiOptions: {
    debugMode: env.enableDebugMode,
    uriOverride: {
      development: env.apiUrl,
      production: env.apiUrl,
    },
  },
  /**
   * The structured-diagnostics sink. HQ shipped without one, and the cost is
   * documented: with no sink the library's delivery level defaults to "warn",
   * `info` reaches nothing, and `debug`/`info` are never console-mirrored — so
   * the reconnect-auth hang arrived as a human saying "the screen is frozen"
   * rather than as a `diagnostics.warn` anyone could see, and a flagless
   * feature store left `null` produced literally no output anywhere.
   *
   * See src/diagnostics/diagnosticsConfig.ts for the full contract and the
   * per-level routing (bounded warn/error ring buffer + boot-window `info`).
   */
  diagnostics: hqDiagnosticsOptions,
  enableAuthStore: { debugMode: false, authStorageKey, refreshStorageKey },
  channelStoreDebugMode: env.enableDebugMode,
  disableLogsStore: env.isProduction && !env.enableDebugMode,
  disableViewStore: false,
  enableRouterStore: true,
  routerStoreProps: {
    // The Developer Dashboard is the landing page (2026-10-26): `/`, post-login
    // navigation and the auth fallbacks all resolve here. "Home" (the Command
    // Center) stays reachable at /home and in the nav. WHICH board opens is the
    // dashboard's own per-user choice (deep link → my default → platform board
    // for admins → own → org boards), so a non-admin lands on the seeded org
    // board unless they own a private one.
    defaultRoute: "Dashboard",
    debugMode: env.enableDebugMode,
    routes: routesList,
    layouts: layoutsList,
    historyEnabled: true,
    // The single boundary where HQ's AUTHORING route table crosses into the
    // library RouterStore's contract. The two `RouteType`s are deliberately
    // different shapes and neither is assignable to the other: HQ's declares
    // `name`/`path` OPTIONAL (the library requires them) and its `DialogType`
    // requires a `title` the library's omits. `as unknown as` is required —
    // a direct `as` is rejected for insufficient overlap. Runtime is unaffected;
    // `makeRoute` fills in what the library expects.
    // NOTE: `routeGuards` is NOT set here, and is NOT appended in
    // createRootStore.ts either — HQ's page-access guard needs the RootStore
    // that is still being constructed, so the composition root appends it
    // itself (stores/RootStore.ts, `createPageAccessRouteGuard`). Adopting the
    // library RootStore — which forwards `routeGuards` verbatim and does NOT
    // append — is what moves that append into createRootStore.ts.
  } as unknown as RootStoreProps["routerStoreProps"],
  appStores: appStores,
  // HQ's ServiceViewConfig is a rich app type (`summaryCards` is a
  // `(rootStore) => SummaryCardConfig[]` FACTORY); the library's is a loose
  // carrier with an index signature, and an interface never gains an implicit
  // one — so the two are not assignable and this stays a cast. It moved OUT of
  // the composition root (libraries-adoption item 3): one cast, at the
  // configuration boundary where the two type systems actually meet.
  serviceStores: serviceStores as unknown as RootStoreProps["serviceStores"],
  appLayoutStore: {
    toolboxBase: "job",
    rightNavButtonTitle: "Client",
    rightNavDefaultRoute: "ClientDetail",
    navGroups: HQ_NAV_GROUPS,
    // The OmniBar is HQ's only navigation (no sidebar): its empty view is the
    // page index (Favorites → Recent → every page by group), and its personal
    // lists are kept per signed-in user, so a shared browser never shows the
    // previous person's favorites or recent pages.
    omnibar: { emptyView: "navigator", userScoped: true },
  },
};

export default rootStoreConfig;
